import { useMemo } from "react"
import type { TenantSlice, TileResult } from "@csbi/contracts"
import { PREV_COLUMN_SUFFIX } from "@csbi/contracts"
import { useFilterStore } from "../filters/model/filter-store.js"
import { useTileQuery, type TileSpec } from "../tiles/hooks/use-tile-query.js"
import { buildComponents, summarise, type HealthComponent } from "./health-score.js"

/**
 * ⚠ **`dau` and `mau` cannot share one query.** A tile asks for one granularity, and these two metrics do not
 * live at the same one: `mau` is written per calendar month, so a combined tile resolves to days and returns
 * `mau: null` in every row — an empty column that looks like "this customer has no monthly actives" rather
 * than "you asked the wrong question". Found exactly that way; hence the separate spec with its own
 * granularity below.
 */

const ADOPTION: TileSpec = { tileKey: "health.adoption", metrics: ["module_adoption_rate"], splitBy: ["module"] }
const MAU: TileSpec = { tileKey: "health.mau", metrics: ["mau"], splitBy: ["period"], granularity: "month" }
const DAU: TileSpec = { tileKey: "health.dau", metrics: ["dau"], splitBy: ["period"] }

export type TenantHealth = {
  tenant: TenantSlice
  components: HealthComponent[]
  score: number | null
  available: number
  total: number
}

const tenantKey = (r: TileResult) => r.columns.find((c) => c.key.endsWith(".tenant_id"))?.key
const measureKey = (r: TileResult, metric: string) => r.columns.find((c) => c.key.endsWith(`.${metric}`))?.key

function byTenant(result: TileResult | undefined, metric: string): Map<string, number[]> {
  const out = new Map<string, number[]>()
  if (!result) return out
  const tk = tenantKey(result)
  const mk = measureKey(result, metric)
  if (!tk || !mk) return out
  for (const row of result.rows) {
    const id = String(row[tk] ?? "")
    const n = Number(row[mk])
    if (!id || !Number.isFinite(n)) continue
    const bucket = out.get(id)
    if (bucket) bucket.push(n)
    else out.set(id, [n])
  }
  return out
}

function prevByTenant(result: TileResult | undefined, metric: string): Map<string, number[]> {
  if (!result) return new Map()
  const mk = measureKey(result, metric)
  if (!mk) return new Map()
  const synthetic: TileResult = {
    ...result,
    columns: [...result.columns, { key: `${mk}${PREV_COLUMN_SUFFIX}`, title: "prev", role: "measure" }],
  }
  return byTenant(synthetic, `${metric}${PREV_COLUMN_SUFFIX}`)
}

const sum = (ns: readonly number[]) => ns.reduce((a, b) => a + b, 0)

export function useClientHealth() {
  const filters = useFilterStore((s) => s.filters)
  const adoption = useTileQuery(ADOPTION, filters)
  const mau = useTileQuery(MAU, filters)
  const dau = useTileQuery({ ...DAU }, { ...filters, comparePrevious: true })

  return useMemo(() => {
    const results = [adoption.data, mau.data, dau.data]
    const tenants = new Map<string, TenantSlice>()
    for (const r of results) for (const t of r?.perTenant ?? []) tenants.set(t.tenantId, t)

    const rates = byTenant(adoption.data, "module_adoption_rate")
    const mauByTenant = byTenant(mau.data, "mau")
    const dauByTenant = byTenant(dau.data, "dau")
    const dauPrev = prevByTenant(dau.data, "dau")

    const rows: TenantHealth[] = [...tenants.values()].map((tenant) => {
      const dauValues = dauByTenant.get(tenant.tenantId) ?? []
      const prevValues = dauPrev.get(tenant.tenantId) ?? []
      const mauValues = mauByTenant.get(tenant.tenantId) ?? []

      const components = buildComponents({
        adoptionRates: rates.get(tenant.tenantId) ?? [],
        avgDau: dauValues.length > 0 ? sum(dauValues) / dauValues.length : null,
        mau: mauValues.length > 0 ? (mauValues.at(-1) ?? null) : null,
        dauNow: dauValues.length > 0 ? sum(dauValues) : null,
        dauPrev: prevValues.length > 0 ? sum(prevValues) : null,
      })

      return { tenant, components, ...summarise(components) }
    })

    return {
      rows: rows.sort((a, b) => (b.score ?? -1) - (a.score ?? -1)),
      isLoading: adoption.isLoading || mau.isLoading || dau.isLoading,
      errorMessage: adoption.errorMessage ?? mau.errorMessage ?? dau.errorMessage,
    }
  }, [adoption.data, mau.data, dau.data, adoption.isLoading, mau.isLoading, dau.isLoading, adoption.errorMessage, mau.errorMessage, dau.errorMessage])
}
